import React, { useState, useEffect, useCallback } from "react";

// libraries
import { List, Modal, Button, Input, Icon } from "semantic-ui-react";

import { pickBy, get, isEmpty } from "lodash";
import PropTypes from "prop-types";
import { List as VirtualizedList, AutoSizer } from "react-virtualized";

// Redux
import { connect } from "react-redux";
import genericAction from "@Actions/generic";
import Loading from "@Modals/Loading";

// Styles
import styles from "./styles.module.css";

const ClientSelectorModal = ({
	setOpen,
	setClient,
	search,
	dispatch,
	isLoading,
	apiConfig,
	ui,
	title,
	representativeId,
	showListNumber = false,
	showCourseSection = false,
	showParentRelationship = false,
	omitIds = [],
	parentId = null,
	multiple = false,
	selectedClients = [],
	width = "60%",
}) => {
	const [searchTerm, setSearchTerm] = useState(search || "");
	const [currentSearch, setCurrentSearch] = useState(search || "");
	const [clientList, setClientList] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [page, setPage] = useState(1);
	const [searching, setSearching] = useState(false);

	const isMultiSelect = showParentRelationship || multiple;

	const [selectedClientIds, setSelectedClientIds] = useState(() => {
		if (isMultiSelect && Array.isArray(selectedClients)) {
			return selectedClients
				.map((c) => {
					return c && (c.id ?? c);
				})
				.filter(Boolean);
		}
		return [];
	});

	const [initialLoad, setInitialLoad] = useState(false);
	const [clientCountTotal, setClientCountTotal] = useState(0);

	const size = 12;

	const computedTitle =
		title ??
		(ui && ui.title) ??
		(showCourseSection ? "Seleccionar Curso" : showParentRelationship ? "Seleccionar Padres" : "Seleccionar Estudiantes");

	const uiConfig = {
		title: computedTitle,
		searchPlaceholder: ui?.searchPlaceholder || ui?.placeholder || (showParentRelationship ? "Buscar Padres..." : "Buscar Estudiantes..."),
		searchButtonText: "Buscar",
		...ui,
	};

	const fetchClients = useCallback(
		async (pageNumber, searchQuery = "") => {
			const queryString = new URLSearchParams(
				pickBy(
					{
						orderKey: apiConfig.orderKey || (showCourseSection ? "name" : "fullName"),
						orderDir: "asc",
						page: pageNumber,
						rows: size,
						all: searchQuery || undefined,
						fields: searchQuery ? apiConfig.fields || ["name", "email", "phone", "tags.name"] : undefined,
						representativeId: representativeId !== null ? representativeId : undefined,
						parentId: parentId !== null ? parentId : undefined,
						isActive: !isEmpty(apiConfig.extraParams) ? undefined : 1,
						activeAttendance: apiConfig.activeAttendance ?? undefined,
						"id[]!": omitIds && omitIds.length > 0 ? omitIds : undefined,
						...(apiConfig.extraParams || {}),
					},
					(value) => {
						return value !== undefined;
					}
				)
			).toString();

			try {
				const response = await dispatch(
					genericAction({
						method: "GET",
						route: `${apiConfig.searchClients}?${queryString}`,
						body: {},
						emptyResponseType: [],
						useFormData: false,
						file: null,
						withLoading: false,
					})
				);

				let items = response?.items;
				let count = response?.count;

				if (!items && response?.data && Array.isArray(response.data)) {
					items = response.data;
					count = response.data.length;
				} else if (!items && Array.isArray(response)) {
					items = response;
					count = response.length;
				}

				if (items && items.length > 0) {
					const idKey = apiConfig?.idKey || "id";
					const nameKey = apiConfig?.nameKey || "name";
					items = items.map((item) => {
						const resolvedId = get(item, idKey) || item.id;
						const resolvedName = get(item, nameKey) || item.fullName || item.name || "";
						return {
							...item,
							id: resolvedId,
							fullName: resolvedName,
							name: resolvedName,
						};
					});
				}

				setClientCountTotal(count || 0);
				return items || [];
			} catch (error) {
				return [];
			}
		},
		[dispatch, apiConfig.searchClients, apiConfig.activeAttendance]
	);

	const performSearch = useCallback(
		async (searchQuery = "") => {
			setSearching(true);
			setClientList([]);
			setPage(1);
			setHasMore(true);
			setClientCountTotal(0);

			let newClients = await fetchClients(1, searchQuery);

			if (searchQuery && newClients.length > 0) {
				const query = searchQuery.toLowerCase();
				const filtered = newClients.filter((c) => {
					return (
						(c.name && c.name.toLowerCase().includes(query)) ||
						(c.fullName && c.fullName.toLowerCase().includes(query)) ||
						(c.id && String(c.id).includes(query))
					);
				});
				newClients = filtered;
			}

			if (newClients.length > 0) {
				setClientList(newClients);
				setPage(2);

				if (newClients.length < size) {
					setHasMore(false);
				}
			} else {
				setHasMore(false);
			}

			setSearching(false);
		},
		[fetchClients]
	);

	const loadMore = useCallback(async () => {
		if (searching || !hasMore) return;

		setSearching(true);

		const newClients = await fetchClients(page, currentSearch);

		if (newClients.length > 0) {
			setClientList((prev) => {
				return [...prev, ...newClients];
			});
			setPage((prev) => {
				return prev + 1;
			});

			if (newClients.length < size) {
				setHasMore(false);
			}
		} else {
			setHasMore(false);
		}

		setSearching(false);
	}, [searching, hasMore, page, currentSearch, fetchClients]);

	const handleClearSearch = () => {
		setSearchTerm("");
	};

	const handleSearch = () => {
		setCurrentSearch(searchTerm);
		performSearch(searchTerm);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSearch();
		}
	};

	const handleRowsRendered = ({ stopIndex }) => {
		if (stopIndex >= clientList.length - 2 && hasMore && !searching && clientList.length >= size) {
			loadMore();
		}
	};

	const renderListContent = () => {
		if (searching && clientList.length === 0) {
			return (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						color: "#666",
						fontSize: "1.5rem",
						fontWeight: "bold",
						flexDirection: "column",
						gap: "10px",
					}}>
					<span>Cargando Resultados</span>
					<div
						style={{
							display: "flex",
							gap: "2px",
						}}>
						<span className={styles.loadingDot} style={{ animationDelay: "0s" }}>
							.
						</span>
						<span className={styles.loadingDot} style={{ animationDelay: "0.16s" }}>
							.
						</span>
						<span className={styles.loadingDot} style={{ animationDelay: "0.32s" }}>
							.
						</span>
					</div>
				</div>
			);
		}

		if (clientList.length === 0 && !searching) {
			return (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						color: "#666",
						fontSize: "1.5rem",
						fontWeight: "bold",
					}}>
					{currentSearch ? "No hay resultados para esta búsqueda" : ""}
				</div>
			);
		}

		return (
			<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
				<div style={{ height: `${clientList.length * 60}px`, maxHeight: "62vh", overflow: "hidden" }}>
					<AutoSizer>
						{({ width: autoSizerWidth, height }) => {
							return (
								<VirtualizedList
									height={height}
									width={autoSizerWidth}
									rowCount={clientList.length}
									rowHeight={60}
									rowRenderer={rowRenderer}
									onRowsRendered={handleRowsRendered}
									className={styles.virtualList}
								/>
							);
						}}
					</AutoSizer>
				</div>
				{isMultiSelect && (
					<div style={{ padding: "12px 0 4px", display: "flex", justifyContent: "center", gap: "8px" }}>
						<Button
							primary
							content="Agregar"
							disabled={selectedClientIds.length === 0}
							onClick={() => {
								const selectedItems = clientList.filter((c) => {
									return selectedClientIds.includes(c.id);
								});
								const alreadySelected = Array.isArray(selectedClients) ? selectedClients : [];
								const nonLoadedSelected = alreadySelected.filter((c) => {
									return (
										selectedClientIds.includes(c.id) &&
										!clientList.some((item) => {
											return item.id === c.id;
										})
									);
								});
								setClient([...nonLoadedSelected, ...selectedItems]);
								setOpen(false);
							}}
							className={styles.selectButton}
						/>
					</div>
				)}
				{searching && clientList.length > 0 && (
					<div
						style={{
							textAlign: "center",
							padding: "3px",
							color: "#666",
							fontSize: "1.3rem",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "8px",
							flexShrink: 0,
						}}>
						<span>Cargando más resultados</span>
						<div
							style={{
								display: "flex",
								gap: "2px",
							}}>
							<span className={styles.loadingDot} style={{ animationDelay: "0s" }}>
								.
							</span>
							<span className={styles.loadingDot} style={{ animationDelay: "0.16s" }}>
								.
							</span>
							<span className={styles.loadingDot} style={{ animationDelay: "0.32s" }}>
								.
							</span>
						</div>
					</div>
				)}
			</div>
		);
	};

	useEffect(() => {
		if (!initialLoad) {
			setInitialLoad(true);
			setCurrentSearch(search || "");
			performSearch(search || "");
		}
	}, [performSearch, initialLoad, search]);

	const rowRenderer = ({ index, key, style }) => {
		const client = clientList[index];
		const isEven = index % 2 === 0;

		// Si showCourseSection es true y el client tiene section, mostrar nombre - sección para cursos
		const courseDisplayName =
			showCourseSection && client.section ? `${client.name} - ${client.section}` : client.fullName || client.name || "";
		// Si showParentRelationship es true y el client tiene relationship, mostrar nombre - relación (para padres)
		const parentDisplayName =
			showParentRelationship && (client?.relationship || client?.fullName) ? `${client.fullName} - ${client.relationship || ""}` : "";
		const displayName = `${parentDisplayName || courseDisplayName} - Id: ${client.id}`;

		const isChecked = selectedClientIds.includes(client.id);
		return (
			<div
				key={key}
				style={{
					...style,
					backgroundColor: isEven ? "#fff" : "#f9f9f9",
					borderBottom: "1px solid #ddd",
					transition: "background 0.2s",
				}}>
				<List.Item
					onClick={(e) => {
						if (isMultiSelect) {
							if (e?.detail >= 2) {
								return;
							}
							setSelectedClientIds((prev) => {
								return prev.includes(client.id)
									? prev.filter((id) => {
											return id !== client.id;
									  })
									: [...prev, client.id];
							});
							return;
						}
						// preserve original behavior out of parent mode
						setClient(client);
					}}
					onDoubleClick={() => {
						if (!isMultiSelect) {
							return;
						}
						setSelectedClientIds([client.id]);
						setClient([client]);
						setOpen(false);
					}}
					className={`${styles.clientList} ${styles.clientListItem} ${styles.virtualListRow}`}
					style={{ position: "relative" }}>
					<List.Content>
						{isMultiSelect && (
							<div
								style={{
									position: "absolute",
									right: 10,
									top: "50%",
									transform: "translateY(-50%)",
									zIndex: 1,
								}}>
								<input
									type="checkbox"
									checked={isChecked}
									onClick={(e) => {
										e.stopPropagation();
									}}
									onChange={() => {
										setSelectedClientIds((prev) => {
											return prev.includes(client.id)
												? prev.filter((id) => {
														return id !== client.id;
												  })
												: [...prev, client.id];
										});
									}}
								/>
							</div>
						)}
						<List.Header className={styles.clientName}>
							{uiConfig.renderName ? uiConfig.renderName(client, displayName) : displayName}
						</List.Header>
						{uiConfig.renderDetails ? (
							<div className={styles.clientDetails}>{uiConfig.renderDetails(client)}</div>
						) : showParentRelationship ? (
							<div className={styles.clientDetails}>
								{get(client, "email", "") ? `${get(client, "email", "")}` : ""}
								{get(client, "email", "") && get(client, "phone", "") ? " | " : ""}
								{get(client, "phone", "") ? `${get(client, "phone", "")}` : ""}
							</div>
						) : (
							<>
								{!(showCourseSection && client.section) && (
									<div className={styles.clientDetails}>
										{get(client, "course[0].fullName", "")}
										{client.code ? ` | ${client.code}` : ""}
										{showListNumber && get(client, "course[0].listNumber", null) !== null
											? ` | No. ${get(client, "course[0].listNumber", "")}`
											: ``}
									</div>
								)}
								{showCourseSection && client.section && null}
							</>
						)}
					</List.Content>
				</List.Item>
			</div>
		);
	};

	const handleBackdropClick = () => {
		setOpen(false);
	};

	const getResponsiveWidth = () => {
		const { innerWidth } = window;
		if (innerWidth <= 450) return "100%";
		if (innerWidth <= 768) return "80%";
		if (innerWidth <= 1200) return "60%";
		return width;
	};

	const getResponsiveHeight = () => {
		const { innerWidth } = window;
		if (innerWidth <= 450) return "100%";
		return "80%";
	};

	return (
		<>
			{isLoading && <Loading isFetching={isLoading} />}
			<Modal
				open
				onClose={handleBackdropClick}
				closeIcon
				className={styles.clientSelectorModal}
				style={{
					width: getResponsiveWidth(),
					height: getResponsiveHeight(),
					margin: "0 auto",
				}}>
				<Modal.Header className={styles.header}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							width: "100%",
						}}>
						<span>{uiConfig.title}</span>
						{clientCountTotal > 0 && <span className={styles.countBadge}>Cant.{clientCountTotal}</span>}
					</div>
				</Modal.Header>

				<Modal.Content>
					<div style={{ marginBottom: "10px", position: "relative" }}>
						<Input
							fluid
							placeholder={uiConfig.searchPlaceholder}
							value={searchTerm}
							onChange={(e) => {
								return setSearchTerm(e.target.value);
							}}
							onKeyDown={handleKeyDown}
							className={styles.filterInput}
							action>
							<input />
							{searchTerm && (
								<button
									type="button"
									onClick={handleClearSearch}
									style={{
										position: "absolute",
										right: "80px",
										top: "50%",
										transform: "translateY(-50%)",
										cursor: "pointer",
										padding: "5px",
										zIndex: 10,
										color: "#999",
										fontSize: "16px",
										background: "none",
										border: "none",
										borderRadius: "50%",
									}}
									onMouseEnter={(e) => {
										e.target.style.color = "#333";
									}}
									onMouseLeave={(e) => {
										e.target.style.color = "#999";
									}}>
									<Icon name="close" />
								</button>
							)}
							<Button className={styles.searchButton} icon="search" onClick={handleSearch} loading={searching} disabled={searching} />
						</Input>
					</div>

					<div className={styles.clientListContainer} style={{ height: "70vh" }}>
						{renderListContent()}
					</div>
				</Modal.Content>
			</Modal>
		</>
	);
};

ClientSelectorModal.propTypes = {
	setOpen: PropTypes.func.isRequired,
	setClient: PropTypes.func.isRequired,
	search: PropTypes.string,
	title: PropTypes.string,
	representativeId: PropTypes.number,
	parentId: PropTypes.number,
	dispatch: PropTypes.func.isRequired,
	isLoading: PropTypes.bool,
	showListNumber: PropTypes.bool,
	showCourseSection: PropTypes.bool,
	showParentRelationship: PropTypes.bool,
	apiConfig: PropTypes.shape({
		searchClients: PropTypes.string.isRequired,
		fields: PropTypes.arrayOf(PropTypes.string),
		orderKey: PropTypes.string,
		activeAttendance: PropTypes.number,
		idKey: PropTypes.string,
		nameKey: PropTypes.string,
		extraParams: PropTypes.object,
	}).isRequired,
	ui: PropTypes.object,
	omitIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	multiple: PropTypes.bool,
	selectedClients: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	width: PropTypes.string,
};

function mapStateToProps({ loading, company }) {
	return {
		isLoading: loading.isLoading,
		showListNumber: get(company, "settings.showListNumber", false),
	};
}

export default connect(mapStateToProps)(ClientSelectorModal);
